import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle, ArrowLeft } from 'lucide-react';

const SM_CDN = 'https://cdn.sportmonks.com/';
function smSrc(src) {
  if (!src || typeof src !== 'string') return src;
  if (/^https?:\/\//.test(src)) return src;
  if (/(^|\/)images\//.test(src)) return SM_CDN + src.replace(/^\/+/, '');
  return src;
}

export function Spinner({ size = 18 }) {
  return <Loader2 size={size} className="animate-spin i-mute" />;
}

export function SectionError({ message, onRetry }) {
  return (
    <div className="i-card p-8 flex flex-col items-center gap-3 text-center">
      <AlertTriangle size={26} className="i-danger" />
      <p className="i-mute text-sm">{message || 'Could not load this section.'}</p>
      {onRetry && (
        <button className="i-btn i-ghost-btn" onClick={onRetry}>Retry</button>
      )}
    </div>
  );
}

export function SkeletonCard({ className }) {
  return <div className={'i-skel ' + (className || '')} style={{ height: 120 }} />;
}

export function SkeletonHero() {
  return <div className="i-skel i-skel-hero" />;
}

export function Card({ title, action, children, bodyClass }) {
  return (
    <section className={'i-card ' + (bodyClass || 'p-4')}>
      {(title || action) && (
        <header className="i-card-h">
          {title && <h3 className="i-fg font-semibold text-sm">{title}</h3>}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function InfoRow({ label, value }) {
  return (
    <div className="i-row">
      <span className="i-mute">{label}</span>
      <span className="i-fg text-right">{value == null || value === '' ? '—' : value}</span>
    </div>
  );
}

function statScalar(v) {
  if (v == null) return null;
  if (typeof v !== 'object') return v;
  if (v.all && typeof v.all === 'object' && 'count' in v.all) return v.all.count;
  if ('value' in v) return v.value;
  if ('total' in v) return v.total;
  if ('count' in v) return v.count;
  if ('average' in v) return v.average;
  return null;
}

export function StatBox({ label, value, big }) {
  const s = statScalar(value);
  return (
    <div className="stat-tile">
      <div className="stat-tile-l">{label}</div>
      <div className={'stat-tile-v' + (big ? ' text-2xl' : '')}>{s == null ? '—' : s}</div>
    </div>
  );
}

export function Pill({ children, className }) {
  return <span className={'i-pill ' + (className || '')}>{children}</span>;
}

export function Logo({ src, alt, size = 40, fallback }) {
  const [err, setErr] = useState(false);
  if (!src || err) {
    const letter = (fallback || alt || '?').trim().charAt(0).toUpperCase();
    return (
      <span className="i-avatar" style={{ width: size, height: size, fontSize: size * 0.4 }}>
        {letter}
      </span>
    );
  }
  return (
    <img src={smSrc(src)} alt={alt || ''} className="i-img" style={{ width: size, height: size }} onError={() => setErr(true)} />
  );
}

export function Flag({ src, alt, size = 18 }) {
  const [err, setErr] = useState(false);
  if (!src || err) return <span className="i-flag-fallback" style={{ width: size, height: size }} />;
  return (
    <img src={smSrc(src)} alt={alt || ''} className="i-flag" style={{ width: size, height: size }} onError={() => setErr(true)} />
  );
}

export function BackButton({ to, label }) {
  const navigate = useNavigate();
  return (
    <button className="i-back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(to))}>
      <ArrowLeft size={14} /> {label}
    </button>
  );
}

export function EntityLink({ to, className, children }) {
  if (!to) return <span className={className}>{children}</span>;
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

export function TabBar({ tabs, active, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);
  return (
    <div className="i-tabs" ref={ref}>
      {tabs.map((t) => (
        <button
          key={t}
          className={'i-tab' + (t === active ? ' on' : '')}
          onClick={() => onChange(t)}
          type="button"
        >
          {t}
        </button>
      ))}
    </div>
  );
}

export function RawDataView({ data, label = 'All data' }) {
  if (data == null) return <p className="i-dim text-sm">No data.</p>;
  return (
    <Card title={label}>
      <div className="rdv-root">
        <RawNode label="data" value={data} />
      </div>
    </Card>
  );
}

function RawNode({ label, value }) {
  if (value == null) {
    return <div className="rdv-row"><span className="rdv-key">{label}:</span> <span className="i-dim">null</span></div>;
  }
  if (typeof value === 'object') {
    const isArr = Array.isArray(value);
    const size = isArr ? value.length : Object.keys(value).length;
    const entries = isArr ? value.map((v, i) => [i, v]) : Object.entries(value);
    return (
      <details>
        <summary className="rdv-summary">{label} {isArr ? `[${size}]` : `{${size}}`}</summary>
        <div className="rdv-children">
          {entries.map(([k, v]) => (
            <RawNode key={String(k)} label={String(k)} value={v} />
          ))}
        </div>
      </details>
    );
  }
  let display = String(value);
  if (display.length > 240) display = display.slice(0, 240) + '…';
  return <div className="rdv-row"><span className="rdv-key">{label}:</span> <span className="rdv-val">{display}</span></div>;
}
